import {AnalysisGraphQL} from 'analysis-tool/AnalysisTypes';
import {ANALYSIS_VISUALIZATION_TYPE_TO_ICON} from 'analysis-tool/charting/AnalysisChartingConstants';
import {getNullableStepFromAnalysis} from 'analysis-tool/data/getStepFromAnalysis';
import {
  AnalysisRightSidebarItem,
  AnalysisRightSidebarItemType,
} from 'analysis-tool/right-sidebar/RightSidebarTypes';
import {AnalysisVisualizationType} from 'analysis-tool/ui/charting/ChartingTypes';
import {getAnalysisActionHistory} from 'analysis-tool/ui/debugAnalysisActionHistory';
import {
  AnalysisMainViewItem,
  AnalysisMainViewItemType,
  ViewReference,
} from 'analysis-tool/ui/main-view/MainViewTypes';
import {assert, assertAndContinue} from 'Asserts';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {STEP_TYPE_TO_USER_FACING_NAME} from 'graphql-client/__generated/GeneratedEnumMaps';
import {
  PipelineStepDataKind,
  PipelineStepStatusCode,
  PipelineStepType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  pipelineStepIsStepType,
  pipelineStepRawDataIsKind,
  PipelineGraphQL,
  PipelineViewGraphQL,
  PipelineViewLineageNode,
} from 'pipelines/PipelineTypes';
import {
  allParentViewsSucceeded,
  getOutputDataForView,
  isDataFrameView,
  isFileView,
  summarizeView,
} from 'pipelines/PipelineViewUtils';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {assertNever} from 'util/Types';

/**
 * Assert that a view exists for the given view reference, with detailed debugging context.
 *
 * @returns The view (type-narrowed to non-null)
 */
export function assertViewExists(
  view: PipelineViewGraphQL | null,
  analysis: AnalysisGraphQL,
  viewReference: ViewReference
): asserts view is PipelineViewGraphQL {
  // Stringify the extras as JSON to avoid Sentry's depth limit truncation
  assert(view != null, 'Expected view to exist', {
    instructions:
      "This error's extras have been stringified to avoid Sentry's depth limit truncation, please parse for analyzing the info.",
    debugInfo: JSON.stringify({
      viewReference,
      analysisId: analysis.id,
      viewCount: analysis.views.length,
      views: analysis.views.map((v) => summarizeView(v)),
      recentActions: getAnalysisActionHistory(),
    }),
  });
}

export const BLOCKED_FAILED_VIEW_ICON = 'ban';
export const EMPTY_VIEW_ICON = 'exclamation-triangle';
export const FAILED_VIEW_ICON = 'exclamation-circle';
export const RUNNING_VIEW_ICON = 'spinner-third';

/** Get the icon for a given view.
 *
 * Icons are based on the last node's status and the type of raw data it contains.
 * - Layout icons are either specified by their config or the default layout icon
 * - Succeeded views' icons are based on the raw data's type
 * - Other views' icons are based on their status
 */
export function getIconForView(analysis: AnalysisGraphQL, view: PipelineViewGraphQL): BenchlingIconName {
  const lastNode = view.lineage[view.lineage.length - 1];

  if (pipelineStepIsStepType(PipelineStepType.LAYOUT, 0, lastNode.step)) {
    return (lastNode.step.config?.icon as BenchlingIconName) ?? 'chart-tree-map';
  } else if (lastNode.step.statusCode === PipelineStepStatusCode.FAILED) {
    return allParentViewsSucceeded(analysis, view) ? FAILED_VIEW_ICON : BLOCKED_FAILED_VIEW_ICON;
  } else if (lastNode.step.statusCode === PipelineStepStatusCode.AI_PROPOSED) {
    return 'sparkle';
  } else if (
    lastNode.step.statusCode === PipelineStepStatusCode.BLOCKED ||
    lastNode.step.statusCode === PipelineStepStatusCode.RUNNING ||
    lastNode.step.statusCode === PipelineStepStatusCode.WAITING
  ) {
    return RUNNING_VIEW_ICON;
  } else if (lastNode.step.statusCode === PipelineStepStatusCode.SUCCEEDED) {
    const outputData = getOutputDataForView(view);

    if (outputData == null) {
      return EMPTY_VIEW_ICON;
    }

    if (pipelineStepRawDataIsKind(outputData, PipelineStepDataKind.CHART)) {
      if (pipelineStepIsStepType(PipelineStepType.CHART_VIEW, 0, lastNode.step)) {
        return ANALYSIS_VISUALIZATION_TYPE_TO_ICON[lastNode.step.config!.chartConfig.type];
      } else if (pipelineStepIsStepType(PipelineStepType.REGRESSION, 0, lastNode.step)) {
        return ANALYSIS_VISUALIZATION_TYPE_TO_ICON[AnalysisVisualizationType.SCATTER_PLOT];
      } else {
        return ModelTypeConstants.CHART;
      }
    } else if (pipelineStepRawDataIsKind(outputData, PipelineStepDataKind.DATASET)) {
      return ModelTypeConstants.DATA_FRAME;
    } else if (pipelineStepRawDataIsKind(outputData, PipelineStepDataKind.FILE)) {
      return ModelTypeConstants.FILE;
    } else {
      assertAndContinue(false, 'Unsupported raw data kind', {rawData: assertNever(outputData)});
    }
  } else {
    assertAndContinue(false, 'Unsupported status', {status: assertNever(lastNode.step.statusCode)});
  }

  return 'question';
}

/**
 * Get the last node in the lineage that is successful, or return `null` if there is no successful nodes.
 */
export function getLastSuccessfulNode(view: PipelineViewGraphQL): PipelineViewLineageNode | null {
  return view.lineage.findLast(({step}) => step.statusCode === PipelineStepStatusCode.SUCCEEDED) ?? null;
}

/**
 * Get the title for a given lineage node.
 *
 * This function only supports successful nodes. It retrieves the title of a node by looking at the output
 * raw data for that node.
 */
export function getTitleForLineageNode(node: PipelineViewLineageNode): string {
  if (
    !assertAndContinue(
      node.step.statusCode === PipelineStepStatusCode.SUCCEEDED,
      'Function only supports successful nodes',
      {
        stepStatusCode: node.step.statusCode,
        stepType: node.step.stepType,
      }
    )
  ) {
    return getTitleForStatus(node.step.statusCode);
  }

  const stepOutputReference = node.stepOutputReference;

  if (
    stepOutputReference == null ||
    stepOutputReference.rawDataIndex == null ||
    !assertAndContinue(
      stepOutputReference.rawDataIndex < stepOutputReference.outputStepData.rawDatas.length,
      'rawDataIndex is out of bounds'
    )
  ) {
    return STEP_TYPE_TO_USER_FACING_NAME[node.step.stepType];
  }

  const rawData = stepOutputReference.outputStepData.rawDatas[stepOutputReference.rawDataIndex];

  if (
    pipelineStepRawDataIsKind(rawData, PipelineStepDataKind.CHART) ||
    pipelineStepRawDataIsKind(rawData, PipelineStepDataKind.DATASET) ||
    pipelineStepRawDataIsKind(rawData, PipelineStepDataKind.FILE)
  ) {
    return rawData.name;
  } else {
    throw new Error(`Unknown raw data kind type: ${assertNever(rawData)}`);
  }
}

/**
 * Get the title for a given status.
 *
 * Although this function supports all status codes, it should only be called with non-successful statuses.
 * For successful lineage nodes, use `getTitleForLineageNode` instead.
 */
export function getTitleForStatus(status: PipelineStepStatusCode): string {
  switch (status) {
    case PipelineStepStatusCode.SUCCEEDED:
      assertAndContinue(false, 'getTitleForStatus should not be called with a successful status');
      return 'Succeeded view';
    case PipelineStepStatusCode.FAILED:
      return 'Failed view';
    case PipelineStepStatusCode.AI_PROPOSED:
    case PipelineStepStatusCode.BLOCKED:
    case PipelineStepStatusCode.RUNNING:
    case PipelineStepStatusCode.WAITING:
      return 'Running view';
    default:
      throw new Error(`Unknown status: ${status}`);
  }
}

/**
 * Get the title for a given view.
 *
 * This function works as follows:
 *  - If the last node is a chart, table, regression, or layout step, and its config exists, get the title from the
 *  config
 *  - Gets the last successful node in the lineage and returns its title. @see getTitleForLineageNode for details
 *  - If there are no successful nodes, returns the title based on the status of the last node. @see getTitleForStatus for details
 *  - If there are no nodes, throw a warning, and return `Analysis view` as a default.
 */
export function getTitleForView(view: PipelineViewGraphQL): string {
  const lastNode = view.lineage[view.lineage.length - 1];
  const lastStep = lastNode?.step;
  if (lastStep?.config != null) {
    if (pipelineStepIsStepType(PipelineStepType.CHART_VIEW, 0, lastStep)) {
      return lastStep.config.chartName;
    } else if (pipelineStepIsStepType(PipelineStepType.TABLE_VIEW, 0, lastStep)) {
      return lastStep.config.viewName;
    } else if (pipelineStepIsStepType(PipelineStepType.REGRESSION, 0, lastStep)) {
      if (lastNode.stepOutputReference!.outputStepData.name === 'Output chart') {
        return lastStep.config.name;
      } else {
        return `${lastStep.config.name} | ${lastNode.stepOutputReference!.outputStepData.name}`;
      }
    } else if (pipelineStepIsStepType(PipelineStepType.LAYOUT, 0, lastStep)) {
      return lastStep.config.name;
    }
  }

  const lastSuccessfulNode = getLastSuccessfulNode(view);
  if (lastSuccessfulNode != null) {
    return getTitleForLineageNode(lastSuccessfulNode);
  } else if (view.lineage.length > 0) {
    return getTitleForStatus(view.lineage[view.lineage.length - 1].step.statusCode);
  } else {
    assertAndContinue(false, 'Expected views to have at least one lineage node.');
    return 'Analysis view';
  }
}

export function getEditViewRightSidebarItemForLineageNode(
  lineageNode: PipelineViewLineageNode
): AnalysisRightSidebarItem<AnalysisRightSidebarItemType.EDIT_VIEW> | null {
  return {
    type: AnalysisRightSidebarItemType.EDIT_VIEW,
    viewReference: {
      stepId: lineageNode.step.id,
      outputReference:
        lineageNode.stepOutputReference != null
          ? {
              stepDataName: lineageNode.stepOutputReference.outputStepData.name,
              rawDataIndex: lineageNode.stepOutputReference.rawDataIndex,
            }
          : null,
    },
  };
}

export function getEditViewRightSidebarItemForView(
  view: PipelineViewGraphQL
): AnalysisRightSidebarItem<AnalysisRightSidebarItemType.EDIT_VIEW> | null {
  const lastNode = view.lineage[view.lineage.length - 1];
  if (lastNode == null) {
    return null;
  }
  return getEditViewRightSidebarItemForLineageNode(lastNode);
}

export function getMainViewItemForLineageNode(
  lineageNode: Omit<PipelineViewLineageNode, '__typename'>
): AnalysisMainViewItem<AnalysisMainViewItemType.DATA_VIEW> {
  return {
    type: AnalysisMainViewItemType.DATA_VIEW,
    viewReference: {
      stepId: lineageNode.step.id,
      outputReference:
        lineageNode.stepOutputReference != null
          ? {
              stepDataName: lineageNode.stepOutputReference.outputStepData.name,
              rawDataIndex: lineageNode.stepOutputReference.rawDataIndex,
            }
          : null,
    },
  };
}

export function getMainViewItemForView(view: PipelineViewGraphQL): AnalysisMainViewItem {
  const lastNode = view.lineage[view.lineage.length - 1];
  if (lastNode == null) {
    return {
      type: AnalysisMainViewItemType.EMPTY_ANALYSIS,
    };
  }

  if (lastNode.step.stepType === PipelineStepType.LAYOUT) {
    return {
      type: AnalysisMainViewItemType.DATA_LAYOUT,
      stepId: lastNode.step.id,
    };
  }

  return getMainViewItemForLineageNode(lastNode);
}

/**
 * Return the view that contains corresponds with the given view reference.
 *
 * A view corresponds when one of its lineage nodes:
 *   1. Uses the same step
 *   2. Has the same step data name
 *   3. Either has the same raw data index, or has a null raw data index
 *
 * A null raw data index indicates a step that hasn't succeeded, which we'll still want to match against.
 */
export function getViewForViewReference(
  analysis: AnalysisGraphQL,
  {stepId, outputReference}: ViewReference
): PipelineViewGraphQL | null {
  if (outputReference == null) {
    const views = analysis.views.filter(({lineage}) =>
      lineage.some(({step: viewStep}) => viewStep.id === stepId)
    );
    assertAndContinue(
      views.length <= 1,
      'Expected to find at most one view for step when outputReference is null',
      {views, stepId, outputReference}
    );
    return views.length === 1 ? views[0] : null;
  }

  const views = analysis.views.filter(({lineage}) =>
    lineage.some(
      ({step: viewStep, stepOutputReference: viewStepOutputReference}) =>
        viewStep.id === stepId &&
        viewStepOutputReference != null &&
        viewStepOutputReference.outputStepData.name === outputReference.stepDataName &&
        (viewStepOutputReference.rawDataIndex === outputReference.rawDataIndex ||
          // Because analysis.views and viewReferences can get out of sync, if we try to view a 'null' raw data index and
          // there is an otherwise matching view with a non-null raw data index, we default to matching index 0.
          (viewStepOutputReference.rawDataIndex === 0 && outputReference.rawDataIndex == null) ||
          viewStepOutputReference.rawDataIndex == null)
    )
  );

  assertAndContinue(views.length <= 1, 'Expected to find at most one view for stepId and outputReference', {
    views,
    stepId,
    outputReference,
  });
  return views.length === 1 ? views[0] : null;
}

/**
 * Define steps that although they take some input, are considered "top-level" in an analysis instead of being
 * rendered as the child of a view
 *
 * Keep in sync with pipelines/views/view_utils.py:TOP_LEVEL_VIEW_STEP_TYPES
 */
export const TOP_LEVEL_ANALYSIS_VIEW_STEP_TYPES = [
  PipelineStepType.JOIN,
  PipelineStepType.PROMOTE_DATASET,
  PipelineStepType.THIRD_PARTY,
  PipelineStepType.UNION_DATASETS,
  PipelineStepType.LOOK_UP,
  PipelineStepType.USER_DEFINED_FUNCTION,
];

/**
 * Return true if the given view is a top-level view.
 *
 * A top-level view is a view that is not the child of another view or whose first node's step is a step
 * in the `TOP_LEVEL_ANALYSIS_VIEW_STEP_TYPES` list.
 *
 * Keep in sync with pipelines/views/view_utils.py:isTopLevelView
 *
 * @see TOP_LEVEL_ANALYSIS_VIEW_STEP_TYPES for the list of steps that promote a view to top-level.
 */
export function isTopLevelAnalysisView(view: PipelineViewGraphQL, pipeline: PipelineGraphQL): boolean {
  if (
    !assertAndContinue(view.lineage.length > 0, 'Expected a single step in the lineage of an analysis view')
  ) {
    return view.lineage[0].step == null;
  }

  const firstStep = view.lineage[0].step;

  return (
    firstStep.incomingEdges.length === 0 ||
    (firstStep.incomingEdges.length > 1 && firstStep.stepType !== PipelineStepType.LAYOUT) ||
    TOP_LEVEL_ANALYSIS_VIEW_STEP_TYPES.includes(firstStep.stepType) ||
    // A view whose parent relationship is ambiguous should be treated as top-level. This occurs
    // when an incoming edge has fromStepDataIndex == null and the parent step data has multiple
    // outputs. The null index indicates a multi-to-multi connection where the edge represents
    // "all outputs flow into this input" rather than a specific output. When there are multiple
    // parent outputs, getChildViewsForView cannot determine which parent output this view belongs
    // to. If there's only 1 output, the relationship is unambiguous despite the null index.
    (firstStep.incomingEdges.length > 0 &&
      firstStep.incomingEdges.every((edge) => {
        if (edge.fromStepDataIndex != null) {
          return false;
        }
        const parentStep = getNullableStepFromAnalysis(pipeline, edge.fromStep.id);
        const parentStepData = parentStep?.outputStepDatas.find((sd) => sd.id === edge.fromStepData.id);
        return parentStepData != null && parentStepData.rawDatas.length > 1;
      }))
  );
}

/**
 * Return true if the given view is a top-level view and outputs a data frame.
 */
export function isTopLevelDataFrameView(view: PipelineViewGraphQL, pipeline: PipelineGraphQL): boolean {
  return isTopLevelAnalysisView(view, pipeline) && isDataFrameView(view);
}

/**
 * Return true if the given view is a top-level view and outputs a file.
 */
export function isTopLevelFileView(view: PipelineViewGraphQL, pipeline: PipelineGraphQL): boolean {
  return isTopLevelAnalysisView(view, pipeline) && isFileView(view);
}

/**
 * Steps that were treated as top-level steps in the legacy sidebar.
 * TODO(BNCH-125215): Remove this.
 */
const LEGACY_TOP_LEVEL_ANALYSIS_VIEW_STEP_TYPES = [
  PipelineStepType.EXPORT_DATA_CATALOG_DATASET,
  PipelineStepType.SAVE_TO_RESULTS,
  PipelineStepType.SEND_TO_DOCUMENT,
];
/**
 * Returns true if the view is a layout view (last step is LAYOUT).
 */
export function isLayoutView(view: PipelineViewGraphQL): boolean {
  const lastNode = view.lineage[view.lineage.length - 1];
  return lastNode.step.stepType === PipelineStepType.LAYOUT;
}

export function isLegacyTopLevelAnalysisView(view: PipelineViewGraphQL): boolean {
  if (
    !assertAndContinue(view.lineage.length > 0, 'Expected a single step in the lineage of an analysis view')
  ) {
    return view.lineage[0].step == null;
  }

  const firstStep = view.lineage[0].step;

  return LEGACY_TOP_LEVEL_ANALYSIS_VIEW_STEP_TYPES.includes(firstStep.stepType);
}
